import ResourcesDispatcher from "../config/ResourcesDispatcher";
import Cell from "../gameUtil/Base/Cell";
import Reel from "../gameUtil/Base/Reel";

export default class GameAnimations {

    static testMethod() {
        console.log("✅=^.^= GameAnimations library is connected successfully!");
    }

    //Il take event del gioco posso gestirlo tramite get e set
    static resetTakeEvent(getTakeEvent: () => Phaser.Time.TimerEvent | undefined, setTakeEvent: (newValue: Phaser.Time.TimerEvent | undefined) => void) {
        const currentEvent = getTakeEvent();

        if (currentEvent) {
            currentEvent.remove(false);
        }

        setTakeEvent(undefined);
    }

    static playWinningTween(scene: Phaser.Scene, tweenedValues: { value: number; scale: number }, win: number, endScale: number, duration: number, winAmountText: Phaser.GameObjects.Text,
        soundManager: any, soundToPlay: any,changeStateMachineCallback: () => void, turbo: boolean, getTakeEvent: () => Phaser.Time.TimerEvent | undefined, setTakeEvent: (newVal: Phaser.Time.TimerEvent | undefined) => void,
        onUpdateCallback: (tweenedValues: { value: number; scale: number }) => void, onCompleteCallback: () => void) {

        return scene.tweens.add({
            targets: tweenedValues,
            value: win,
            scale: endScale,
            duration,
            ease: "Power1",
            onStart: () => {
                this.resetTakeEvent(getTakeEvent, setTakeEvent);
                winAmountText.setVisible(true);
                if (!turbo) {
                    soundManager.playCoinsSound();
                } else {
                    soundManager.play(soundToPlay);
                }

                changeStateMachineCallback();
            },
            onUpdate: () => {
                onUpdateCallback(tweenedValues);
            },
            onComplete: () => {
                onCompleteCallback();
            }
        });
    }

    static async playWinCombinationAnimation(animatedCells: Cell[], unanimatedCells: Cell[], lineIndex: number, moreThanOneLine: boolean, startWinningCombinationsTextTween: (loop: boolean) => void,
        stopWinningCombinationsTextTween: () => void): Promise<void> {

        // Mostra e anima le celle
        animatedCells.forEach((cell) => {
            cell.turnOn();
            cell.showFrame();
        });

        unanimatedCells.forEach((cell) => {
            cell.turnOff();
            cell.showFrame();
        });

        startWinningCombinationsTextTween(!moreThanOneLine);

        if (moreThanOneLine) {
            await Promise.all(animatedCells.map((cell) => cell.playAnimation(false)));

            stopWinningCombinationsTextTween();

            animatedCells.forEach((cell) => {
                cell.turnOff();
                cell.hideFrame();
            });

            unanimatedCells.forEach((cell) => {
                cell.turnOff();
                cell.hideFrame();
            });
        } else {
            animatedCells.forEach((cell) => {
                cell.playAnimation(true);
            });
        }
    }

    // funzione che si occupa di ciclare le animazioni di vincita di qualunque combinazione
    static processWinningCellAnimation(reelIndex: number, cellIndex: number, animatedCells: any[], unanimatedCells: any[], cell: any, expanding?: Cell): void {
        if (cellIndex > 0) {
            const wildInfo: any[] = (window as any).GameDataManager.getReelSpecialInfo()?.filter((wildInfo: { reelIndex: number }) => wildInfo.reelIndex === reelIndex);
            // caso in cui c'è l'extra wild che si espande
            if (wildInfo?.length > 0 && expanding) {
                animatedCells.push(expanding);
            } 
            // caso in cui la cella extra wild si moltiplica
            else {
                animatedCells.push(cell);
            }
        } else {
            unanimatedCells.push(cell);
        }
    }

    static async animateAllSymbolsWithMemo(reels: any[], memo: number): Promise<void> {
        const animatedCells: any[] = [];

        reels.forEach((reel) => {
            reel.cells.forEach((cell: any) => {
                if (cell.getMemo() === memo) {
                    animatedCells.push(cell);
                }
            });
        });

        await Promise.all(animatedCells.map((cell) => cell.playAnimation(false)));
    }

    static async animateAllSymbolsWithMemoList(reels: any[], memos: number[]): Promise<void> {
        const animatedCells: any[] = [];

        reels.forEach((reel) => {
            reel.cells.forEach((cell: any) => {
                if (memos.includes(cell.getMemo())){
                    animatedCells.push(cell);
                }
            });
        });

        await Promise.all(animatedCells.map((cell) => cell.playAnimation(false)));
    }

    static async animateWilds(reels: Reel[], expanding: boolean): Promise<void> {
        const wildInfoList = (window as any).GameDataManager.getReelSpecialInfo();
        const winningCombinations = (window as any).GameDataManager.getWinningCombinations();

        if (!wildInfoList.length) return;

        const animations = wildInfoList.map((wildInfo: { reelIndex: number, wild: number }) => {
            const { reelIndex, wild } = wildInfo;
            const reelIsWinning = winningCombinations?.length > 0 && winningCombinations.some((combo: any) => combo.positions[reelIndex] > 0);
            
            // se è un expanding wild usa la gestione del wild che si espande, altrimenti usa la gestione delle celle animate con extra wild
            expanding ? this.extraWildExpanding(reels, reelIndex, wild, reelIsWinning) : this.extraWildInCells(reels, reelIndex, wild, reelIsWinning);

            return;
        });

        await Promise.all(animations);
    }

    static extraWildExpanding(reels: Reel[], reelIndex: number, cellIndex: number, reelIsWinning: boolean){
        const expandingWild = reels[reelIndex].expandingWild;
        expandingWild.setIndex(cellIndex);
        expandingWild.expandWild(reelIsWinning);
    }

    static extraWildInCells(reels: Reel[], reelIndex: number, cellIndex: number, reelIsWinning: boolean){
        const cellsWithExtraWild = reels[reelIndex].getCells();

        cellsWithExtraWild.forEach((ew: Cell, index: number) => {
            if(index>0 && index <= ResourcesDispatcher.GAME_CONSTANTS.NUM_OF_CELLS_PER_REEL){
                ew.setIndex(cellIndex);
                ew.setCellInfo(ResourcesDispatcher.GAME_CONSTANTS.S_EXTRA_WILD);
                ew.extraWildAnimation(reelIsWinning);
            }
        })
    }

    static async contractWilds(gameArea: any): Promise<void> {
        let expandingWilds: any[] = [];

        (window as any).GameDataManager.getReelSpecialInfo().forEach((wildInfo: any) => {
            const expandingWild = gameArea.reels[wildInfo.reelIndex].expandingWild;
            expandingWild.setIndex(wildInfo.cellIndex);
            expandingWilds.push(expandingWild);
        });

        await Promise.all(expandingWilds.map((wild) => wild.contractWild()));
    }


    static playFreeSpinWinningAnimation(reels: any[], numCell: number) {
        const animatedCells: any[] = [];

        reels.forEach((reel) => {
            for (let i = 1; i < numCell; i++) {
                animatedCells.push(reel.freeSpinCells[i]);
            }
        });

        animatedCells.forEach((cell) => cell.playAnimation(true));
    }

    static setWinningLines(combinations: any) : any[]{
        let winningLines: any[] = [];
        for (let i = 0; i < combinations.length; i++) {
            winningLines.push({
                symbolPositions: combinations[i].positions,
                index: combinations[i].numOfLine,
                repetitions: combinations[i].repeat,
                symbolIndex: combinations[i].symbol,
                win: combinations[i].win,
                animated: false,
                scatterLine: false
            });
        }

        return winningLines;
    }

    static stopWinCombinationAnimated(animatedCells : any[]){
        animatedCells?.forEach((cell) => {
            cell.stopAnimation();
            cell.hideFrame();
            cell.turnOff();
        });
    }

    static stopWinCombinationUnanimated(unanimatedCells: any[]){
        unanimatedCells?.forEach((cell) => {
            cell.hideFrame();
            cell.turnOff();
        });
    }
}